<template>
  <data-grid-remote
    :total-rows="fetchTotalRows"
    :server-url="serverUrl"
    :data-fetch-callback-fn="dataFetchCallbackFn"
    :fetchOptions="fetchOptions"
    :useFetchOptions="useFetchOptions"
    :pageSize="pageSize"
    :currentPage="currentPage"
    :paginationOptions="paginationOptions"
    @update:currentPage="onPageUpdate"
    @update:pageSize="onPageSizeUpdate"
    @update:sort="onSortChange"
    data-test="remote-grid"
  >
    <template v-slot:header>
      <div class="filters-container">
        <div class="filter-bar-container">
          <f-filter-bar
            v-model="filterValue"
            @update:modelValue="updateFilters"
            data-test="filter-bar"
          >
            <f-filter-bar-category
              v-if="filters.case_types?.length > 1"
              :title="t('activerecord.models.case_type', { count: 1 })"
              name="case_type_ids"
              :is-searchable="filters.case_types.length > 5"
              is-dialog-persisted
            >
              <f-filter-bar-option
                v-for="(case_type, optionIndex) in filters.case_types"
                :key="optionIndex"
                :title="case_type.name"
                :value="case_type.id"
              />
            </f-filter-bar-category>

            <f-filter-bar-category
              v-if="filters.schools?.length > 1"
              :title="t('activerecord.models.school', { count: 1 })"
              name="school_ids"
              :is-searchable="filters.schools.length > 5"
              is-dialog-persisted
            >
              <f-filter-bar-option
                v-for="(school, optionIndex) in filters.schools"
                :key="optionIndex"
                :title="school.name"
                :value="school.id"
              />
            </f-filter-bar-category>

            <f-filter-bar-category
              v-if="filters.statuses?.length > 1"
              :title="t('activerecord.attributes.case.status')"
              name="statuses"
              is-dialog-persisted
            >
              <f-filter-bar-option
                v-for="(status, optionIndex) in filters.statuses"
                :key="optionIndex"
                :title="status.display_name"
                :value="status.name"
              />
            </f-filter-bar-category>

            <f-filter-bar-category
              v-if="filters.creators?.length > 1"
              :title="t('reports.tasks.show.created_by')"
              name="creator_ids"
              :is-searchable="filters.creators.length > 5"
              is-dialog-persisted
            >
              <f-filter-bar-option
                v-for="(creator, optionIndex) in filters.creators"
                :key="optionIndex"
                :title="creator.name"
                :value="creator.id"
              />
            </f-filter-bar-category>

            <f-filter-bar-category
              v-if="filters.students?.length > 1"
              :title="capitalize(t('student'))"
              name="student_ids"
              :is-searchable="filters.students.length > 5"
              is-dialog-persisted
            >
              <f-filter-bar-option
                v-for="(student, optionIndex) in filters.students"
                :key="optionIndex"
                :title="student.name"
                :value="student.id"
              />
            </f-filter-bar-category>

            <f-filter-bar-category
              v-if="filters.assignees?.length > 1"
              :title="t('activerecord.attributes.task.assignees', { count: 2 })"
              name="assignee_ids"
              :is-searchable="filters.assignees.length > 5"
              is-dialog-persisted
            >
              <f-filter-bar-option
                :title="t('reports.tasks.show.filters.task_assignees.options.unassigned')"
                value="unassigned"
              />
              <f-filter-bar-option
                v-for="(assignee, optionIndex) in filters.assignees"
                :key="optionIndex"
                :title="assignee.name"
                :value="assignee.id"
              />
            </f-filter-bar-category>

            <f-filter-bar-category
              :title="t('reports.tasks.show.filters.task_open_or_completed.title')"
              name="completed_at_method"
            >
              <f-filter-bar-option
                :title="t('reports.tasks.show.filters.task_open_or_completed.options.open')"
                value="never"
              />

              <f-filter-bar-option
                :title="t('reports.tasks.show.filters.task_open_or_completed.options.completed')"
                value="ever"
              />
            </f-filter-bar-category>

            <f-filter-bar-category :title="t('reports.tasks.show.filters.task_due_date.title')" name="task-due-date">
              <f-filter-bar-date
                :title="t('reports.tasks.show.filters.task_due_date.options.from.title')"
                name="due_date_from"
                :max="dueDateTo"
                :active-text-fn="(text) => t('reports.tasks.show.filters.task_due_date.options.from.active_text', { date: text })"
              />
              <f-filter-bar-date
                title="To"
                name="due_date_to"
                :min="dueDateFrom"
                :active-text-fn="(text) => t('reports.tasks.show.filters.task_due_date.options.to.active_text', { date: text })"
              />
            </f-filter-bar-category>

            <f-filter-bar-category :title="t('reports.tasks.show.filters.task_created_date.title')" name="task-created-at">
              <f-filter-bar-date
                :title="t('reports.tasks.show.filters.task_created_date.options.from.title')"
                name="created_at_from"
                :max="createdAtTo || new Date()"
                :active-text-fn="(text) => t('reports.tasks.show.filters.task_created_date.options.from.active_text', { date: text })"
              />
              <f-filter-bar-date
                :title="t('reports.tasks.show.filters.task_created_date.options.to.title')"
                name="created_at_to"
                :min="createdAtFrom"
                :max="new Date()"
                :active-text-fn="(text) => t('reports.tasks.show.filters.task_created_date.options.to.active_text', { date: text })"
              />
            </f-filter-bar-category>

            <f-filter-bar-category
              :title="t('reports.tasks.show.filters.case_opened_date.title')"
              name="case-opened"
            >
              <f-filter-bar-date
                :title="t('reports.tasks.show.filters.case_opened_date.options.from.title')"
                name="case_created_at_from"
                :max="caseCreatedAtTo || new Date()"
                :active-text-fn="(text) => t('reports.tasks.show.filters.case_opened_date.options.from.active_text', { date: text })"
              />

              <f-filter-bar-date
                :title="t('reports.tasks.show.filters.case_opened_date.options.to.title')"
                name="case_created_at_to"
                :min="caseCreatedAtFrom"
                :max="new Date()"
                :active-text-fn="(text) => t('reports.tasks.show.filters.case_opened_date.options.to.active_text', { date: text })"
              />
            </f-filter-bar-category>
          </f-filter-bar>
        </div>
      </div>
    </template>
    <data-grid-column
      :title="t('reports.tasks.show.open_task')"
      data-id="task_path"
      v-slot="value"
    >
      <a class="btn btn--primary-alt feather_btn feather_btn--primary-alt" :href="value">{{ t('reports.tasks.show.link_to_task') }}</a>
    </data-grid-column>

    <data-grid-column
      :title="t('activerecord.models.school', { count: 1 })"
      data-id="school_name"
      v-slot="value"
      sortable
    >
      {{ value }}
    </data-grid-column>

    <data-grid-column
      :title="t('activerecord.models.case', { count: 1 })"
      data-id="case"
      v-slot="value"
      sortable
    >
      <a :href="value.path">{{ value.id }}</a>
    </data-grid-column>

    <data-grid-column
      :title="t('activerecord.attributes.task.assignees', { count: 'many' })"
      data-id="assignees"
      v-slot="value"
      sortable
    >
      {{ value || t('reports.tasks.show.values.task_assignees.unassigned') }}
    </data-grid-column>

    <data-grid-column
      :title="t('reports.tasks.show.due_date')"
      data-id="due_date"
      v-slot="value"
      sortable
    >
      <div v-if="value" :class="['due-date-cell', isOverdue(value) ? 'overdue' : null ]">
        <date-formatted :value="value" pattern="date-time"></date-formatted>
        <svg-icon v-if="isOverdue(value)" icon="alert-circle-fill" :width="30" :height="30" fillColor="#e12336" />
      </div>
      <span v-else>{{ t('reports.common.values.not_available') }}</span>
    </data-grid-column>

    <data-grid-column
      :title="t('activerecord.attributes.task.created_at')"
      data-id="created_at"
      v-slot="value"
      sortable
    >
      <date-formatted v-if="value" :value="value" pattern="date-time"></date-formatted>
      <span v-else>{{ t('reports.common.values.not_available') }}</span>
    </data-grid-column>

    <data-grid-column
      :title="t('reports.tasks.show.created_by')"
      data-id="creator"
      v-slot="value"
      sortable
    >
      {{ value }}
    </data-grid-column>

    <data-grid-column
      :title="t('activerecord.attributes.task.status', { count: 1 })"
      data-id="status"
      v-slot="value"
      sortable
    >
      {{ value }}
    </data-grid-column>

    <data-grid-column
      :title="t('activerecord.models.case_type', { count: 1 })"
      data-id="case_type_name"
      v-slot="value"
      sortable
    >
      {{ value }}
    </data-grid-column>

    <data-grid-column
      :title="t('reports.cases.show.case_opened_date')"
      data-id="case_opened_at"
      v-slot="value"
      sortable
    >
      <date-formatted v-if="value" :value="value" pattern="date-time"></date-formatted>
      <span v-else>{{ t('reports.common.values.not_available') }}</span>
    </data-grid-column>

    <data-grid-column
      :title="t('reports.tasks.show.student_name')"
      data-id="student_name"
      v-slot="value"
      sortable
    >
      <a :href="value.path">{{ value.name }}</a>
    </data-grid-column>

    <data-grid-column
      :title="t('activerecord.attributes.task.description')"
      data-id="description"
      v-slot="value"
    >
      <div class="description-text" v-if="value">
        {{ descriptionText(value) }}
      </div>
    </data-grid-column>
  </data-grid-remote>
</template>

<script setup>
import { computed, ref } from 'vue';
import {
  DataGridColumn,
  DataGridRemote,
  DateFormatted,
  FFilterBar,
  FFilterBarCategory,
  FFilterBarDate,
  FFilterBarOption
} from '@raptor/feather-ui';
import { capitalize } from '~/helpers/string_helpers';
import { csrf } from '~/helpers/application_helpers.js';
import { isOverdue, toDate } from '~/helpers/date_helpers';

// helper methods
const { t } = I18n;

const props = defineProps({
  dataUrl: String,
  filters: Object,
  defaultFilters: Object,
  defaultSort: Object,
});

const DATE_FILTER_NAMES = [
  'due_date_from',
  'due_date_to',
  'created_at_from',
  'created_at_to',
  'case_created_at_from',
  'case_created_at_to',
]

const descriptionText = (text) => {
  const suffix = text.length > 50 ? '...' : '';
  return `${text.substring(0, 50)}${suffix}`;
}

const formattedDefaultFilters = Object.entries(props.defaultFilters).reduce((newObj, [key, value]) => {
  newObj[key] = DATE_FILTER_NAMES.includes(key) ? [toDate(value)] : [value]
  return newObj;
}, {});

// Sorting
const sortBy = ref(props.defaultSort?.sort_by);
const sortOrder = ref(props.defaultSort?.sort_order);

const onSortChange = (newValue) => {
  const primarySort = newValue.at(-1);
  sortBy.value = primarySort.dataId;
  sortOrder.value = primarySort.direction;
};

// Filters
const filterValue = ref(formattedDefaultFilters);
const valueForField = (field) => filterValue.value[field]?.[0];
const dueDateFrom = computed(() => valueForField('due_date_from'));
const dueDateTo = computed(() => valueForField('due_date_to'));
const createdAtFrom = computed(() => valueForField('created_at_from'));
const createdAtTo = computed(() => valueForField('created_at_to'));
const caseCreatedAtFrom = computed(() => valueForField('case_created_at_from'));
const caseCreatedAtTo = computed(() => valueForField('case_created_at_to'));
const assigneeIds = computed(() => filterValue.value.assignee_ids?.filter((v) => v !== 'unassigned'))
const includeUnassigned = computed(() => filterValue.value.assignee_ids?.includes('unassigned'))
const completedAtMethod = computed(() => valueForField('completed_at_method'))

const updateFilters = () => {
  const filters = filterValue.value;
  DATE_FILTER_NAMES.forEach((name) => {
    filters[name] = filters[name]?.slice(-1);
  })

  filters.completed_at_method = filters.completed_at_method?.slice(-1) || [];
};

const filtersForRequest = computed(() => ({
  ...filterValue.value,
  assignee_ids: assigneeIds.value,
  include_unassigned: includeUnassigned.value,
  due_date_from: dueDateFrom.value,
  due_date_to: dueDateTo.value,
  created_at_from: createdAtFrom.value,
  created_at_to: createdAtTo.value,
  case_created_at_from: caseCreatedAtFrom.value,
  case_created_at_to: caseCreatedAtTo.value,
  completed_at_method: completedAtMethod.value,
}));

// Pagination
const paginationOptions = {
  perPageOptions: [5, 10, 15, 20],
};

const pageSize = ref(10);

const onPageSizeUpdate = (newPageSize) => {
  pageSize.value = newPageSize;
};

const currentPage = ref(1);

const onPageUpdate = (newPageNumber) => {
  currentPage.value = newPageNumber;
};

const totalRows = ref(0);
const fetchTotalRows = computed(() => () => totalRows.value);

// Fetch related options
const serverUrl = () => {
  return props.dataUrl;
};

const useFetchOptions = {
  beforeFetch({ options }) {
    options.headers = {
      ...options.headers,
      'Content-Type': 'application/json',
      'X-CSRF-Token': csrf(),
    };
    return { options };
  },
};

const fetchOptions = computed(() => ({
  method: 'post',
  body: {
    page: currentPage.value,
    per_page: pageSize.value,
    filters: filtersForRequest.value,
    sort_by: sortBy.value,
    sort_order: sortOrder.value,
  },
}));

// response handling
const dataFetchCallbackFn = (payload) => {
  if (!payload) return;

  const { data, total } = payload;

  totalRows.value = total || 0;

  return data.tasks?.map((t) => ({
    assignees: t.assignees.join(', '),
    case: t.case,
    case_opened_at: toDate(t.case_opened_at),
    case_type_name: t.case_type,
    created_at: toDate(t.created_at),
    creator: t.creator,
    description: t.description,
    due_date: toDate(t.due_date),
    id: t.id,
    student_name: {
      name: t.student.name,
      path: t.student.path,
    },
    school_name: t.school_name,
    status: t.status,
    task_path: t.path,
  })) || [];
};
</script>

<style lang="scss" scoped>
@import "@raptor/feather-ui/_base.scss";

.due-date-cell {
  display: flex;
  &.overdue {
    color: #e12336;
  }
}
</style>
